// Central SEO map — single source of truth for every route on the site.
// Used by:
//   1. React <Seo /> component at runtime (via useLocation)
//   2. scripts/prerender.mjs at build time (to bake meta tags into raw HTML)
//
// Adding a new route? Add it here AND to public/sitemap.xml.
import { SUBURB_BY_SLUG, SUBURBS } from "./suburbs";
import { CUISINE_BY_SLUG, CUISINES } from "./cuisines";
import { BLOG_BY_SLUG, BLOG_POSTS, BLOG_CATEGORIES } from "./blog";

export const SITE_ORIGIN = "https://restaurantphotographer.com.au";
export const SITE_NAME = "Restaurant Photographer Melbourne";
export const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/brand/og-image-default.webp`;
export const DEFAULT_OG_WIDTH = 1200;
export const DEFAULT_OG_HEIGHT = 630;

// ─── STATIC ROUTES ───────────────────────────────────────
// Same shape as dynamic resolvers below: { title, description, ogImage?, ogTitle?, noindex?, jsonLd? }
const STATIC = {
  "/": {
    title: "Restaurant Photographer Melbourne · Menu, Food & Cafe Photography",
    description:
      "Melbourne restaurant photographer — menu, food & Uber Eats photos from $10/image. On-location, all suburbs. Get a quote in minutes.",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        { "@type": "Question", name: "How much does restaurant menu photography cost in Melbourne?", acceptedAnswer: { "@type": "Answer", text: "Volume-tier pricing: $30 per image for 15–20 images, $25 per image for 21–99 images, and $10 per image once you reach 100+ images (minimum spend $1,000). Most café and restaurant menu shoots land between $540 and $1,000." } },
        { "@type": "Question", name: "Do you come to my restaurant, or is the shoot in a studio?", acceptedAnswer: { "@type": "Answer", text: "Every shoot is on-location at your venue. We bring professional lighting, surfaces and gear to your kitchen — no studio drop-offs, no shipping food across town." } },
        { "@type": "Question", name: "How long does a typical menu shoot take?", acceptedAnswer: { "@type": "Answer", text: "A 15–25 dish shoot takes 3–4 hours on-site. A full 25–60 dish menu library is a single full day on-location (7–8 hours). Larger 60+ image catalogues split across two shorter days." } },
        { "@type": "Question", name: "What is the minimum booking size?", acceptedAnswer: { "@type": "Answer", text: "15 images is the minimum booking. Below 15 it doesn't make economic sense for either side once travel and setup are factored in." } },
        { "@type": "Question", name: "Do you shoot for Uber Eats, DoorDash and Menulog?", acceptedAnswer: { "@type": "Answer", text: "Yes — every menu shoot includes square plus landscape crops, consistent white-balance across dishes, and files named to match your menu items. Files are sized and ready to upload to Uber Eats, DoorDash, Menulog and your Google Business Profile." } },
        { "@type": "Question", name: "When do I get the photos back?", acceptedAnswer: { "@type": "Answer", text: "Edited, web and print-ready files are delivered within 5–7 business days. Urgent turnarounds (3 days) are available on request — mention it during the call." } },
        { "@type": "Question", name: "Which Melbourne suburbs do you cover?", acceptedAnswer: { "@type": "Answer", text: "All of Greater Melbourne. Strongest networks across the western suburbs (Footscray, Point Cook, Tarneit, Truganina, Werribee, Sunshine, Deer Park), plus regular work in the CBD, Carlton, Fitzroy, Brunswick, Richmond, South Yarra, St Kilda, Prahran, Epping, Craigieburn, Preston, Dandenong, Glen Waverley, Clayton and Springvale." } },
        { "@type": "Question", name: "What if I'm not happy with a photo?", acceptedAnswer: { "@type": "Answer", text: "Every shoot includes one free re-edit per dish, no questions asked. If something fundamental needs a re-shoot, we organise that on the next available date." } },
        { "@type": "Question", name: "Can you also shoot the venue and interior?", acceptedAnswer: { "@type": "Answer", text: "Yes — venue and ambience photography is a $450 add-on combined with a menu shoot. Includes front of house, fit-out and service shots. Standalone venue shoots are also available." } },
        { "@type": "Question", name: "Do I need to provide plates, surfaces or styling?", acceptedAnswer: { "@type": "Answer", text: "No. We shoot on your real plates, in your real space — that's the whole point. We bring extra surfaces, props and lighting as needed." } },
      ],
    },
  },
  "/menu-photography": {
    title: "Menu Photography Melbourne | Restaurant Photographer",
    description:
      "Professional menu photography in Melbourne. On-location, all suburbs. Volume pricing from $10/image. Hand-confirmed quotes.",
    jsonLd: serviceWithBreadcrumb("Menu Photography", "On-location menu photography for Melbourne restaurants, cafés and venues.", "/menu-photography", "Menu Photography"),
  },
  "/food-photography": {
    title: "Food Photography Melbourne | Restaurant Photographer",
    description:
      "Editorial food photography in Melbourne. Hero plates, signature dishes and campaign-grade imagery. From $10/image on volume.",
    jsonLd: serviceWithBreadcrumb("Food Photography", "Hero plates and signature dishes for marketing campaigns, openings and menu launches.", "/food-photography", "Food Photography"),
  },
  "/venue-photography": {
    title: "Venue Photography Melbourne | Restaurant Photographer",
    description:
      "Interior, fit-out and dining-room photography for Melbourne restaurants and venues. On-location. Hand-confirmed quotes.",
    jsonLd: serviceWithBreadcrumb("Venue Photography", "Front of house, fit-out and ambience photography for Melbourne hospitality venues.", "/venue-photography", "Venue Photography"),
  },
  "/brand-photography": {
    title: "Brand & Campaign Photography Melbourne | Restaurant Photographer",
    description:
      "Multi-frame brand and campaign photography for Melbourne hospitality — openings, PR, franchise rollouts and editorials.",
    jsonLd: serviceWithBreadcrumb("Brand and Campaign Photography", "Brand and campaign photography for Melbourne restaurants — openings, expansions, franchise partners and PR.", "/brand-photography", "Brand Photography"),
  },
  "/ubereats-photography": {
    title: "Uber Eats Photography Melbourne | Delivery Menu Photos",
    description:
      "Square-crop, app-graded photography for Uber Eats, DoorDash and Menulog. Lift menu rank and conversion. From $10/image.",
    jsonLd: serviceWithBreadcrumb("Delivery App Food Photography", "Delivery-app menu photography for Uber Eats, DoorDash and Menulog — square crops, consistent white-balance, hero-first ordering.", "/ubereats-photography", "Uber Eats Photography"),
  },
  "/cafe-photography": {
    title: "Cafe Photographer Melbourne | Brunch & Coffee Photography",
    description:
      "Daylight-led café photography across Melbourne — brunch plates, latte art, venue interior. From $10/image on volume.",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Service",
          "@id": `${SITE_ORIGIN}/cafe-photography#service`,
          serviceType: "Cafe Photography",
          provider: { "@id": `${SITE_ORIGIN}/#business` },
          areaServed: { "@type": "City", name: "Melbourne" },
          description: "Café photography across Melbourne — brunch plates, latte art, pastry case, interior frames.",
          url: `${SITE_ORIGIN}/cafe-photography`,
        },
        breadcrumbList([
          { name: "Home", item: `${SITE_ORIGIN}/` },
          { name: "Cafe Photography", item: `${SITE_ORIGIN}/cafe-photography` },
        ]),
        {
          "@type": "FAQPage",
          mainEntity: [
            { "@type": "Question", name: "When is the best time to shoot a café in Melbourne?", acceptedAnswer: { "@type": "Answer", text: "Almost always 7–10am before the brunch rush, or after 2pm when the room quietens down. North-facing windows on Brunswick's Sydney Road and Collingwood's Smith Street give the best soft daylight — we tend to start at 7:30am." } },
            { "@type": "Question", name: "Do we need to close the café?", acceptedAnswer: { "@type": "Answer", text: "Almost never. We shoot around service — usually a quiet pre-service window (7:30–9am) for hero plates and latte art, then candid counter and venue shots between 10:30 and 11:30 if you'd like real customers in frame." } },
            { "@type": "Question", name: "Can you shoot the coffee machine and barista in action?", acceptedAnswer: { "@type": "Answer", text: "Yes — pour shots on a La Marzocco or Synesso, latte art top-down and from the side, espresso pulls with crema detail, hands on the portafilter. We bring continuous LED lighting that doesn't blow out crema or flatten a rosetta." } },
            { "@type": "Question", name: "How many photos do we get from a 2–4 hour shoot?", acceptedAnswer: { "@type": "Answer", text: "Typically 25–50 finished frames covering brunch plates (8–12), coffee & latte (6–10), counter / pastry case (6–10), fit-out / street-front (5–8) plus a handful of detail shots." } },
            { "@type": "Question", name: "How much does café photography cost in Melbourne?", acceptedAnswer: { "@type": "Answer", text: "Short morning shoots from $450 covering brunch hero plates (4–6 dishes), coffee detail and a venue sweep. Full menu refreshes from $540 (8–12 dishes). Larger venues with extensive all-day menus from $900–$1,200." } },
            { "@type": "Question", name: "Will the photos work for Google Business Profile and Instagram?", acceptedAnswer: { "@type": "Answer", text: "Yes. Every café shoot includes 1:1 square crops, 4:5 vertical for the feed, 9:16 vertical for Stories and Reels, and files sized for Google Business cover (1080×608) and gallery uploads." } },
          ],
        },
      ],
    },
  },
  "/google-business-profile-photos": {
    title: "Google Business Profile Photos Melbourne | Restaurant Photographer",
    description:
      "Cover, interior, exterior and dish photography sized for Google Business Profile and Maps. Lift local search visibility.",
    jsonLd: serviceWithBreadcrumb("Google Business Profile Photography", "Cover, interior, exterior and dish photography optimised for Google Business Profile and Google Maps listings.", "/google-business-profile-photos", "Google Business Profile Photos"),
  },
  "/restaurant-reels": {
    title: "Restaurant Reels Melbourne | Short-Form Video Photography",
    description:
      "Short-form vertical reels for Melbourne restaurants and cafés — pours, plating, service moments. Instagram, TikTok, Google.",
    jsonLd: serviceWithBreadcrumb("Restaurant Video Reels", "Short-form vertical video production for Melbourne restaurants — Instagram, TikTok, Google Business reels.", "/restaurant-reels", "Restaurant Reels"),
  },
  "/packages": {
    title: "Restaurant Photography Packages Melbourne | Pricing & Inclusions",
    description:
      "Three transparent restaurant photography packages — menu refresh, full library, brand campaign. From $10/image on volume.",
    jsonLd: packagesItemListLd(),
  },
  "/about": {
    title: "About | Restaurant Photographer Melbourne",
    description:
      "Melbourne restaurant photographer. Hospitality-focused, kitchen-credible, on-location. Built for menus that actually sell food.",
  },
  "/contact": {
    title: "Contact | Restaurant Photographer Melbourne",
    description:
      "Call 0494 756 111 or send a brief — most Melbourne shoot quotes are confirmed by phone within five minutes.",
  },
  "/get-a-quote": {
    title: "Get a Quote | Restaurant Photographer Melbourne",
    description:
      "Five-block qualifying form for Melbourne restaurant photography quotes. Hand-confirmed by phone within one business day.",
  },
  "/locations": {
    title: "Melbourne Restaurant Photography Locations | All Suburbs",
    description:
      "On-location restaurant photographer covering 23 Melbourne suburbs across Inner, West, North and South-East. No travel fees in metro.",
  },
  "/blog": {
    title: "Blog | Restaurant Photography Tips & Guides Melbourne",
    description:
      "Honest writing on Melbourne restaurant photography — pricing, briefing, suburb-by-suburb, cuisine-specific guides. 14 articles.",
  },
  "/menu-shoot-cost-planner": {
    title: "Menu Shoot Cost Planner | Free Tool | Restaurant Photographer Melbourne",
    description:
      "Cost your menu shoot before you call. Interactive Melbourne pricing planner — dishes, drinks, venue, reels. Quoted in 60 seconds.",
    jsonLd: webAppLd({
      name: "Menu Shoot Cost Planner",
      description: "Interactive Melbourne pricing planner — dishes, drinks, venue, reels. Quoted in 60 seconds.",
      path: "/menu-shoot-cost-planner",
      faqs: [
        { q: "Is the planner price accurate for a 22-dish Footscray restaurant or a 60-dish CBD venue?", a: "Yes — accurate within ~5% for both. A 22-dish Vietnamese venue in Footscray with menu + Uber Eats + Instagram typically lands at $850–$920. A 60-dish two-trade CBD venue with full menu + Reels + Google Business typically lands at $1,950–$2,100." },
        { q: "Why does the per-image rate drop from $30 to $10 at 100+ images?", a: "Production overhead per image goes down dramatically as menu size grows. The lighting setup, surface prep and colour grading workflow are amortised across more shots. We pass the saving on directly rather than keep it as margin." },
        { q: "Can I bolt Reels, Google Business or drone exterior onto the planner output?", a: "Yes — Reels packs from $180 (3 clips) to $620 (15 clips), Google Business asset packs from $240, drone exterior $220 for CASA-approved sub-25kg sorties. Each toggle in the planner has its own line." },
        { q: "Do you ever quote higher than the planner figure?", a: "Only for genuinely bespoke briefs — multi-venue groups (3+ sites), regional travel outside Greater Melbourne, urgent <24-hour turnarounds, OOH-grade brand campaigns with talent, or unusual access (rooftop, multi-floor)." },
        { q: "Is GST included in the planner figure?", a: "No — all numbers in the planner are ex-GST. GST is added on the invoice at the current ATO rate of 10%. So a $980 planner figure invoices at $1,078 inc-GST." },
      ],
    }),
  },
  "/menu-shoot-planner": {
    title: "Menu Shoot Planner | Free Tool | Restaurant Photographer Melbourne",
    description:
      "Plan a menu shoot like a pro — scope, timing, kitchen prep and shoot-day checklist. Free interactive Melbourne planner.",
    jsonLd: webAppLd({
      name: "Menu Shoot Planner",
      description: "Plan a menu shoot like a pro — scope, timing, kitchen prep and shoot-day checklist.",
      path: "/menu-shoot-planner",
      faqs: [
        { q: "How far ahead should I book a menu shoot in Melbourne?", a: "Most venues book 1–3 weeks ahead during the standard February–November window. November to early-December books out 3–4 weeks ahead because every venue is rebuilding their summer menu before Christmas trade. Urgent 3–5 day turnarounds are possible most of the year." },
        { q: "What's the best day of the week to shoot in a working kitchen?", a: "Tuesday to Thursday between 7:30am and 11am is the Melbourne sweet spot — kitchen prep is calmer, the espresso machine is dialled in by 8am, your A-shift chef is on the pass." },
        { q: "Can I add more dishes after I submit the planner?", a: "Yes — up to 24 hours before the shoot day. The planner total recalculates automatically when you change dish counts. After the shoot day is locked, last-minute additions on-site are quoted at the next-tier per-image rate." },
        { q: "Do you provide a shot list before the shoot day?", a: "Yes — we send a numbered shot list 48 hours before the shoot matching your menu items, with a tick-off column for the chef and a target time per dish (typically 8–12 minutes for a complex plate, 3–5 minutes for a side or dessert)." },
      ],
    }),
  },
  "/delivery-app-photo-audit": {
    title: "Delivery App Photo Audit | Free Tool | Restaurant Photographer Melbourne",
    description:
      "Score your Uber Eats menu A+ to D in 90 seconds — six honest questions, instant grade, personalised audit follow-up.",
    jsonLd: webAppLd({
      name: "Delivery App Photo Audit",
      description: "Score your Uber Eats, DoorDash and Menulog menu A+ to D in 90 seconds.",
      path: "/delivery-app-photo-audit",
    }),
  },
  "/photo-needs-calculator": {
    title: "Photo Needs Calculator | Free Tool | Restaurant Photographer Melbourne",
    description:
      "How many restaurant photos do you actually need? Pick menu size and channels — get image count and indicative shoot cost in 60 seconds.",
    jsonLd: webAppLd({
      name: "Photo Needs Calculator",
      description: "How many restaurant photos do you actually need? Pick menu size and channels — get image count and indicative shoot cost.",
      path: "/photo-needs-calculator",
      faqs: [
        { q: "Does this calculator account for Uber Eats versus Google Business image rules?", a: "Yes. Uber Eats uses 1:1 square crops at minimum 968×968px — the calculator matches your live menu 1:1 to dish count. Google Business needs a cover (1080×608), 10 hero dish frames and interior/exterior." },
        { q: "Why does a 28-dish Carlton restaurant return ~58 images and not 28?", a: "Because almost no Melbourne restaurant uses a single image per dish across every channel. A 28-dish menu typically needs 28 web/menu library shots, 28 Uber Eats square crops, 4 Google Business hero images and 11 Instagram supporting frames." },
        { q: "Is the cost estimate accurate for outer-metro suburbs like Werribee, Cranbourne or Sunbury?", a: "Yes — no travel fees inside Greater Melbourne (we cover all 31 LGAs). The estimate is within ~10% for almost every brief." },
      ],
    }),
  },
  "/restaurant-marketing-score": {
    title: "Restaurant Marketing Score | Free Scorecard | Restaurant Photographer Melbourne",
    description:
      "Score your Melbourne restaurant's marketing 0–100 across Google, photography, social, delivery, reviews, ads and email.",
    jsonLd: webAppLd({
      name: "Restaurant Marketing Score",
      description: "Score your Melbourne restaurant's marketing 0–100 across Google, photography, social, delivery, reviews, ads and email.",
      path: "/restaurant-marketing-score",
    }),
  },
  "/privacy": {
    title: "Privacy Policy | Restaurant Photographer Melbourne",
    description:
      "Privacy policy compliant with the Australian Privacy Act 1988 — what we collect, why, who we share with, and your rights.",
    noindex: true,
  },
  "/terms": {
    title: "Terms & Conditions | Restaurant Photographer Melbourne",
    description:
      "Hospitality photography service terms — quotes, deposits, cancellations, image licence, copyright and re-edits. Plain English.",
    noindex: true,
  },
};

// ─── HELPERS ─────────────────────────────────────────────
function serviceLd(serviceType, description) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType,
    provider: { "@id": `${SITE_ORIGIN}/#business` },
    areaServed: { "@type": "City", name: "Melbourne" },
    description,
  };
}

function faqPageLd(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

// Wrap a single-typed JSON-LD with an additional FAQPage node using @graph.
function withFaqs(primaryLd, faqs) {
  const { "@context": _ctx, ...primary } = primaryLd;
  return {
    "@context": "https://schema.org",
    "@graph": [
      primary,
      {
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  };
}

// WebApplication schema for interactive tool pages.
function webAppLd({ name, description, path, faqs }) {
  const url = `${SITE_ORIGIN}${path}`;
  const graph = [
    {
      "@type": "WebApplication",
      "@id": `${url}#webapp`,
      name,
      url,
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web",
      description,
      offers: { "@type": "Offer", price: "0", priceCurrency: "AUD" },
      provider: { "@id": `${SITE_ORIGIN}/#business` },
    },
    breadcrumbList([
      { name: "Home", item: `${SITE_ORIGIN}/` },
      { name, item: url },
    ]),
  ];
  if (faqs?.length) {
    graph.push({
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
  }
  return { "@context": "https://schema.org", "@graph": graph };
}

// Wrap a Service schema with a BreadcrumbList.
function serviceWithBreadcrumb(serviceType, description, path, crumbLabel) {
  const url = `${SITE_ORIGIN}${path}`;
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        "@id": `${url}#service`,
        serviceType,
        provider: { "@id": `${SITE_ORIGIN}/#business` },
        areaServed: { "@type": "City", name: "Melbourne" },
        description,
        url,
      },
      breadcrumbList([
        { name: "Home", item: `${SITE_ORIGIN}/` },
        { name: crumbLabel, item: url },
      ]),
    ],
  };
}

function breadcrumbList(items) {
  return {
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: it.item,
    })),
  };
}

// ItemList schema for /packages.
function packagesItemListLd() {
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "ItemList",
        name: "Restaurant Photography Packages Melbourne",
        description: "Three restaurant photography packages for Melbourne venues — menu refresh, full library, brand campaign.",
        url: `${SITE_ORIGIN}/packages`,
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Menu Refresh", description: "20–30 dishes, fully edited, web and print ready. From $540." },
          { "@type": "ListItem", position: 2, name: "Full Library", description: "40–60 dishes, complete menu + Uber Eats + Google Business coverage." },
          { "@type": "ListItem", position: 3, name: "Brand Campaign", description: "Multi-frame brand and campaign photography for openings, PR, franchise rollouts." },
        ],
      },
      breadcrumbList([
        { name: "Home", item: `${SITE_ORIGIN}/` },
        { name: "Packages", item: `${SITE_ORIGIN}/packages` },
      ]),
    ],
  };
}

const trim = (s, max) => (s.length > max ? s.slice(0, max - 1).trimEnd() + "…" : s);

// ─── DYNAMIC ROUTES ──────────────────────────────────────
function suburbMeta(s) {
  const path = `/restaurant-photographer/${s.slug}`;
  const url = SITE_ORIGIN + path;
  const faqs = [
    { q: `Do you charge travel fees for ${s.name}?`, a: `No — ${s.name} is inside our Greater Melbourne coverage zone. The shoot-day rate is the same whether we're in the CBD or out at ${s.name}. No travel fees, no minimums.` },
    { q: `How quickly can you shoot in ${s.name}?`, a: `Most ${s.name} shoots are booked 7–14 days ahead. Urgent (48–72 hour) turnarounds are usually possible — call directly and we'll confirm the same day.` },
    { q: `Do you understand the local dining scene in ${s.name}?`, a: `Yes — we shoot in ${s.name} regularly. We brief into the local context (cuisine mix, average price point, delivery vs dine-in split) before the call, so the photography matches what the suburb actually rewards.` },
    { q: `Can you handle both menu and venue photography in ${s.name}?`, a: `Yes — every shoot can combine menu, venue, GBP and reels in one day if scoped properly. Most ${s.name} briefs are multi-channel.` },
  ];
  return {
    title: `Restaurant Photographer ${s.name} | Menu & Food Photography`,
    description: `On-location restaurant photographer in ${s.name}, Melbourne. Menu, food, Uber Eats and Google Business Profile photos. Fast turnaround. Get a quote.`,
    ogImage: `${SITE_ORIGIN}/photography/${s.hero}-lg.webp`,
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Service",
          "@id": `${url}#service`,
          name: `Restaurant Photographer ${s.name}`,
          serviceType: "Restaurant photography",
          provider: { "@id": `${SITE_ORIGIN}/#business` },
          areaServed: { "@type": "Place", name: `${s.name}, Melbourne, VIC, Australia` },
          description: s.context,
          url,
          image: `${SITE_ORIGIN}/photography/${s.hero}-lg.webp`,
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_ORIGIN}/` },
            { "@type": "ListItem", position: 2, name: "Locations", item: `${SITE_ORIGIN}/locations` },
            { "@type": "ListItem", position: 3, name: s.name, item: url },
          ],
        },
        {
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        },
      ],
    },
  };
}

// Per-slug clean titles for cuisine pages (avoid awkward "Indian Restaurants Restaurant Photographer" duplication).
const CUISINE_TITLE_OVERRIDES = {
  "indian-restaurants": "Indian Restaurant Photographer Melbourne | Menu & Food Photography",
  "pizza-restaurants": "Pizza Restaurant Photographer Melbourne | Menu & Food Photography",
  "burger-restaurants": "Burger Restaurant Photographer Melbourne | Menu & Food Photography",
  "cafes": "Café Photographer Melbourne | Brunch & Coffee Photography",
  "fine-dining": "Fine Dining Photographer Melbourne | Restaurant Food Photography",
  "bars": "Bar & Venue Photographer Melbourne | Cocktail & Food Photography",
  "dessert-shops": "Dessert & Café Photographer Melbourne | Food Photography",
  "catering": "Catering Photographer Melbourne | Event & Hospitality Photography",
};

function cuisineMeta(c) {
  const path = `/cuisine/${c.slug}`;
  const url = SITE_ORIGIN + path;
  const title = CUISINE_TITLE_OVERRIDES[c.slug] || `${c.name} Restaurant Photographer Melbourne | Food Photography`;
  return {
    title,
    description: `Specialist food photography for ${c.name.toLowerCase()} in Melbourne. Menu photos, Uber Eats imagery and delivery app-ready shots. From $10/image.`,
    ogImage: `${SITE_ORIGIN}/photography/${c.hero}-lg.webp`,
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Service",
          "@id": `${url}#service`,
          name: `${c.name} Photography Melbourne`,
          serviceType: "Restaurant photography",
          provider: { "@id": `${SITE_ORIGIN}/#business` },
          areaServed: { "@type": "City", name: "Melbourne" },
          audience: { "@type": "BusinessAudience", name: c.name },
          description: c.intro,
          url,
          image: `${SITE_ORIGIN}/photography/${c.hero}-lg.webp`,
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_ORIGIN}/` },
            { "@type": "ListItem", position: 2, name: c.name, item: url },
          ],
        },
        {
          "@type": "FAQPage",
          mainEntity: (c.faqs || []).map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        },
      ],
    },
  };
}

function blogMeta(p) {
  const path = `/blog/${p.slug}`;
  const url = SITE_ORIGIN + path;
  const cat = BLOG_CATEGORIES.find((c) => c.id === p.category)?.label || "Article";
  const articleBody = p.sections.flatMap((s) => [s.h, ...s.body]).join(" ");
  const wordCount = articleBody.split(/\s+/).length;
  const heroImageUrl = `${SITE_ORIGIN}/photography/${p.hero}-lg.webp`;
  const keywords = [
    cat,
    "restaurant photography",
    "Melbourne",
    p.title.replace(/[^\w\s]/g, "").split(/\s+/).filter((w) => w.length > 4).slice(0, 4).join(", "),
  ].filter(Boolean).join(", ");
  const graph = [
    {
      "@type": "Article",
      "@id": `${url}#article`,
      mainEntityOfPage: { "@type": "WebPage", "@id": url },
      headline: p.title,
      description: p.dek,
      url,
      image: {
        "@type": "ImageObject",
        url: heroImageUrl,
        width: 1600,
        height: 900,
      },
      articleSection: cat,
      keywords,
      wordCount,
      inLanguage: "en-AU",
      datePublished: p.datePublished || "2026-02-15",
      dateModified: p.dateModified || "2026-07-29",
      author: {
        "@type": "Organization",
        name: SITE_NAME,
        url: SITE_ORIGIN,
      },
      publisher: {
        "@type": "Organization",
        name: SITE_NAME,
        url: SITE_ORIGIN,
        logo: {
          "@type": "ImageObject",
          url: DEFAULT_OG_IMAGE,
          width: DEFAULT_OG_WIDTH,
          height: DEFAULT_OG_HEIGHT,
        },
      },
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_ORIGIN}/` },
        { "@type": "ListItem", position: 2, name: "Journal", item: `${SITE_ORIGIN}/blog` },
        { "@type": "ListItem", position: 3, name: p.title, item: url },
      ],
    },
  ];
  if (p.faqs?.length) {
    graph.push({
      "@type": "FAQPage",
      mainEntity: p.faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
  }
  return {
    title: `${p.title} | Restaurant Photographer Melbourne`,
    description: trim(p.dek, 155),
    ogImage: heroImageUrl,
    jsonLd: { "@context": "https://schema.org", "@graph": graph },
  };
}

// ─── MASTER LOOKUP ───────────────────────────────────────
const SUBURB_PREFIX = "/restaurant-photographer/";
const CUISINE_PREFIX = "/cuisine/";
const BLOG_PREFIX = "/blog/";

/**
 * Return the SEO meta object for a given pathname.
 * Falls back to the homepage meta if the path is unknown (e.g. /404).
 */
export function getSeo(rawPath) {
  const path = normalisePath(rawPath);

  if (STATIC[path]) return { path, ...STATIC[path] };

  if (path.startsWith(SUBURB_PREFIX)) {
    const slug = path.slice(SUBURB_PREFIX.length);
    const s = SUBURB_BY_SLUG[slug];
    if (s) return { path, ...suburbMeta(s) };
  }
  if (path.startsWith(CUISINE_PREFIX)) {
    const slug = path.slice(CUISINE_PREFIX.length);
    const c = CUISINE_BY_SLUG[slug];
    if (c) return { path, ...cuisineMeta(c) };
  }
  if (path === "/blog") return { path: "/blog", ...STATIC["/blog"] };
  if (path.startsWith(BLOG_PREFIX)) {
    const slug = path.slice(BLOG_PREFIX.length);
    const p = BLOG_BY_SLUG[slug];
    if (p) return { path, ...blogMeta(p) };
  }
  return { path: "/", ...STATIC["/"] };
}

function normalisePath(p) {
  if (!p) return "/";
  // strip query / hash
  const clean = p.split("?")[0].split("#")[0];
  // strip trailing slash (except root)
  return clean.length > 1 && clean.endsWith("/") ? clean.slice(0, -1) : clean;
}

/**
 * Every route the site can serve. Used by the prerender script.
 */
export function allPaths() {
  return [
    ...Object.keys(STATIC),
    ...SUBURBS.map((s) => `${SUBURB_PREFIX}${s.slug}`),
    ...CUISINES.map((c) => `${CUISINE_PREFIX}${c.slug}`),
    ...BLOG_POSTS.map((p) => `${BLOG_PREFIX}${p.slug}`),
  ];
}
